import { FC, ReactElement } from 'react';

import { getAktivitet, patchSkyddsklassningPublicera } from 'store/aktivitetSlice';
import { getBebyggelseobjektUtkast } from 'store/bebyggelseobjektUtkastSlice';
import { getPubliceradeBebyggelseobjekt } from 'store/publiceradeBebyggelseobjektSlice';
import { RootState } from 'store/rootReducer';

import { useAppDispatch, useAppSelector } from 'hooks/defaultStoreHooks';
import { useDialog } from 'hooks/useDialog';

import { RaaDialog } from 'raa-react';

import { Aktivitet, ModelError } from 'models';

import { BebyggelseobjektgruppEnum } from 'enums';

import { formatDateWithTime } from 'utilities/dateUtilities';

const PubliceraSkyddsklassningDialog: FC<{ id?: string }> = ({ id }): ReactElement => {
  const dispatch = useAppDispatch();
  const { unsetDialog, setDialog } = useDialog();
  const { requestStatus } = useAppSelector((state: RootState) => state.aktivitet);

  const handlePublicering = async (): Promise<void> => {
    if (id && requestStatus !== 'pending') {
      const response = await dispatch(patchSkyddsklassningPublicera(id));
      unsetDialog();

      if (response.meta.requestStatus === 'rejected') {
        setDialog(RaaDialog, {
          header: 'Kan inte publiceras',
          close: unsetDialog,
          proceed: unsetDialog,
          ariaDescribedBy: 'dialog-description',
          children: (
            <div className="tw-mt-8">
              <p id="dialog-description">
                {payloadIsModelError(response.payload) ? response.payload.message : 'Något gick snett'}
              </p>
            </div>
          )
        });
      } else if (response.meta.requestStatus === 'fulfilled') {
        await dispatch(getAktivitet(id));
        await dispatch(getPubliceradeBebyggelseobjekt(id));
        const updatedUtkast = await dispatch(getBebyggelseobjektUtkast(id))
          .unwrap()
          .then((BoUs) => BoUs.find((BoU) => BoU.bebyggelseobjektgrupp === BebyggelseobjektgruppEnum.SAMMANSATT));

        // 201 PUBLICERAD utan granskning
        if (response.payload === undefined) {
          setDialog(RaaDialog, {
            header: 'Publiceringen godkändes',
            close: unsetDialog,
            proceed: unsetDialog,
            ariaDescribedBy: 'dialog-description',
            children: (
              <p id="dialog-description" className="tw-mt-8">
                Ingen manuell granskning krävdes.
              </p>
            )
          });
          // 202 Skickad till Granska
        } else {
          setDialog(RaaDialog, {
            header: 'Mottagen för granskning',
            close: unsetDialog,
            proceed: unsetDialog,
            ariaDescribedBy: 'dialog-description',
            children: (
              <div className="tw-mt-8">
                <p id="dialog-description">
                  Vi har tagit emot informationen och kommer att publicera den efter godkänd granskning av
                  personuppgifter och sekretess.
                </p>
                {updatedUtkast && (
                  <p>
                    Ärendenummer: {updatedUtkast.registrering.granskning_arendenummer}, mottaget{' '}
                    {formatDateWithTime(updatedUtkast.registrering.status_datum_inskickad)}
                  </p>
                )}
              </div>
            )
          });
        }
      }
    }
  };

  return (
    <RaaDialog
      header={'Skicka till publicering?'}
      proceed={handlePublicering}
      cancel={unsetDialog}
      close={requestStatus === 'idle' ? unsetDialog : undefined}
      showLoadingSpinner={requestStatus === 'pending'}
      proceedButtonText="publicera"
      cancelButtonText={'avbryt'}
      ariaDescribedBy="dialog-description"
    >
      <p id="dialog-description" className="tw-my-4">
        Inskickad information blir offentlig handling och kommer att publiceras efter Riksantikvarieämbetets granskning.
      </p>
    </RaaDialog>
  );
};

const payloadIsModelError = (p?: Aktivitet | ModelError): p is ModelError => !!p && Object.hasOwn(p, 'message');

export default PubliceraSkyddsklassningDialog;
